/* eslint-disable @typescript-eslint/no-explicit-any */
import { proxy, subscribe } from "valtio";
import { wellWishesStore } from "../hooks/store";
import { AsoEbi } from "../types/aso.ebi";
import { OptionMenuItem } from "../types/catering/menu.item";
import { MailType } from "../types/mailcredit";
import { ClientStorage, ClientStorageItem } from "./clientstorage";
import { getCoupleDisplayName } from "./couple.names";
import {
  AccessCards,
  BankAccount,
  BrideAndGroomShorts,
  CoupleNameOrder,
  CountdownSection,
  createDefaultGuestFormConfiguration,
  EventSchedule,
  FAQ,
  GallerySection,
  GetEmailWithRSVPStatus,
  GetPhoneWithRSVPStatus,
  GuestLinkStatus,
  GuestRestrictRSVPStatus,
  LoveWallRestrictionStatus,
  LoveWallStatus,
  MailRemindersMap,
  Menu,
  Meridian,
  PaymentLink,
  Program,
  ResolveRestrictedRSVPWithEmail,
  SendRSVPEmailStatus,
  Template,
  UserWeddingRole,
  Wedding,
  WeddingMainContent,
  WeddingPreference,
  WelcomeNoteDisplayStatus,
} from "../types/wedding";
import { WeddingParty } from "../types/wedding.party";
import { WellWish } from "../types/wellwish";
import { AsoEbiStore } from "./aso.ebi";

export const createDefaultTemplate = (): Template => {
  return "a";
};

export const createDefaultFAQ = (): FAQ => {
  return {
    question: "",
    answer: "",
  };
};

export const createNewGallerySection = (): GallerySection => {
  return {
    sectionTitle: "",
    images: ["", "", "", "", ""],
  };
};

export const createNewWeddingParty = (): WeddingParty => {
  return {
    _id: "",
    name: "",
    imageURL: "",
    role: "",
    party: "",
  };
};

export const createCountDownSection = (): CountdownSection => {
  return {
    fianceeName: "",
    fianceeEmail: "",
    weddingDate: "",
    image: "",
  };
};

export const createDefaultEventSchedule = (
  preferredDate?: Date | string,
): EventSchedule => {
  const now = new Date();
  const preferred = preferredDate ? new Date(preferredDate) : null;
  const eventDate =
    preferred &&
    !Number.isNaN(preferred.getTime()) &&
    preferred.getFullYear() > 1900
      ? preferred
      : now;

  // Account setup collects the wedding day, but not necessarily the event
  // time. Keep the useful current-time default while pre-filling that day.
  if (eventDate !== now) {
    eventDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
  }

  return {
    _id: "",
    eventType: "",
    address: "",
    note: "",
    date: eventDate,
    rawDate: {
      hours: eventDate.getHours(),
      minutes: eventDate.getMinutes(),
      day: eventDate.getDate(),
      month: eventDate.getMonth() + 1,
      year: eventDate.getFullYear(),
    },
    timezone: { name: "WAT", offset: "+01:00" },
    colors: {
      color1: {
        colorCode: "",
        colorName: "",
      },
      color2: {
        colorCode: "",
        colorName: "",
      },
    },
    dressCode: {
      bride: "",
      groom: "",
    },
    hideAddress: false,
    hideFromRSVP: false,
  };
};

export const createDefaultBankAccount = (): BankAccount => {
  return {
    country: "",
    bankName: "",
    accountNumber: "",
    accountName: "",
    currency: "",
    sortCode: "",
  };
};

export const createDefaultBankDetails = (): BankAccount => ({
  country: "",
  bankName: "",
  accountNumber: "",
  accountName: "",
  currency: "",
  sortCode: "",
});

export const createDefaultPaymentLink = (): PaymentLink => ({
  title: "",
  url: "",
});

export const createDefaultProgram = (): Program => {
  return {
    _id: "",
    time: {
      hours: 0x0,
      minutes: 0x0,
      meridian: Meridian.am,
    },
    title: "",
  };
};

export const createDefaultMenu = (): Menu => {
  return {
    _id: "",
    category: "",
    dishes: [""],
  };
};

export const createDefaultMenuOptionItem = (): OptionMenuItem => ({
  _id: "",
  name: "",
  image: "",
  tableIDs: [],
  totalAmount: 0,
  timeToDelivery: 0,
});

function formatDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  return date.toLocaleDateString("en-US", options);
}

export const createDefaultWeddingPreference = (
  brideFirstName: string,
  groomFirstName: string,
  weddingDate: string,
  coupleNameOrder: CoupleNameOrder = "bride-first",
): WeddingPreference => {
  return {
    heroSection: {
      order: [
        "hero_main_text",
        "short_description",
        "wedding_date",
        "rsvp_button",
      ],
      buttonStyle: {
        buttonShape: "pill",
        buttonBorder: "filled",
      },
      fontFamily: {
        hero_main_text: "Gilda Display",
        short_description: "Gopher",
        wedding_date: "Gopher",
        rsvp_button: "Gopher",
      },
      values: {
        hero_main_text: {
          isDefault: true,
          text: getCoupleDisplayName({
            brideFirstName,
            groomFirstName,
            coupleNameOrder,
          }),
        },
        short_description: {
          isDefault: true,
          text: "we are getting married 💍",
        },
        wedding_date: {
          isDefault: true,
          text: weddingDate,
        },
        rsvp_button: {
          isDefault: true,
          text: "RSVP NOW",
        },
      },
      displayStatus: {
        hero_main_text: true,
        short_description: true,
        wedding_date: false,
        rsvp_button: true,
      },
      asoEbiDisplayStatus: true,
      giftingDisplayStatus: true,
    },
    guestFormConfiguration: createDefaultGuestFormConfiguration(),
    pastGuestFormConfiguration: createDefaultGuestFormConfiguration(),
  };
};

export type Editor = {
  template: "a" | "b";
  sendRSVPEmailStatus: SendRSVPEmailStatus;
  ubxdid: string;
  accountID: string;
  weddingID: string;
  accessCards: AccessCards;
  coupleNameOrder: CoupleNameOrder;
  hasSharedWebsite: boolean;
  mainContent: WeddingMainContent;
  weddingPartyOrder: string[];
  brideAndGroomShorts: BrideAndGroomShorts;
  currentEventSchedule: EventSchedule;
  currentEventScheduleIndex: number;
  creatorRole: UserWeddingRole;
  groomFirstName: string;
  groomLastName: string;
  groomEmail: string;
  brideFirstName: string;
  brideLastName: string;
  brideEmail: string;
  groomID: string;
  brideID: string;
  preference: WeddingPreference;
  mailRemindersMap: MailRemindersMap;
  // spouseJoinedAt: string;
  // spouseInvitedAt: string;
  groomInvitedAt: string;
  brideInvitedAt: string;
  eventPlannerInvitedAt: string;
  eventPlannerJoinedAt: string;
  groomJoinedAt: string;
  brideJoinedAt: string;
  eventSchedules: EventSchedule[];
  gallery: GallerySection[];
  personalInformation: string[];
  rsvpRelationships: string[];
  bankAccounts: BankAccount[];
  programme: Program[];
  menu: Menu[];
  paymentLinks: PaymentLink[];
  wishlistURL: string;
  faqs: FAQ[];
  giftingMessage: string;
  currentFAQ: FAQ;
  currentFAQIndex: number;
  guestLinkStatus: GuestLinkStatus;
  maxAdditionalGuestsPerInvite: number;
  guestRestrictRSVPStatus: GuestRestrictRSVPStatus;
  resolveRestrictedRSVPWithEmail: ResolveRestrictedRSVPWithEmail;
  getPhoneWithRSVPStatus: GetPhoneWithRSVPStatus;
  getEmailWithRSVPStatus: GetEmailWithRSVPStatus;
  loveWallStatus: LoveWallStatus;
  loveWallStandaloneStatus: LoveWallStatus;
  loveWallRestrictionStatus: LoveWallRestrictionStatus;
  currentGallery: GallerySection;
  currentGalleryIndex: number;
  isCountdownDrawerOpen: boolean;
  isScheduleDrawerOpen: boolean;
  welcomeNote: string;
  welcomeNoteDisplayStatus: WelcomeNoteDisplayStatus;
  clickedDdayGettingStarted: boolean;
  currentWeddingPartyIndex: number;
  currentWeddingParty: WeddingParty;
  weddingParty: WeddingParty[];
  currentBankAccount: BankAccount;
  currentPaymentLink: PaymentLink;
  currentBankAccountIndex: number;
  currentPaymentLinkIndex: number;
  currentProgram: Program;
  currentMenu: Menu;
  isUpdatingWeddingInfo: boolean;
  showHashTagOnDDay: boolean;
  XAccountHash: string;
  currentMenuOptionItem: OptionMenuItem;
};

export const getEditorStepsFromCurrentEditorState = (
  currentEditorState: Editor,
) => {
  const hashtagComplete = !!(
    currentEditorState?.mainContent?.hashtag &&
    currentEditorState?.mainContent?.coupleImage &&
    currentEditorState?.mainContent?.date
  );
  const loveStoryComplete = !!(
    currentEditorState.brideAndGroomShorts.brideImage &&
    currentEditorState.brideAndGroomShorts.brideShortWords &&
    currentEditorState.brideAndGroomShorts.groomImage &&
    currentEditorState.brideAndGroomShorts.groomShortWords
  );
  const eventSchedulesComplete = !!currentEditorState.eventSchedules.length;
  const weddingPartyComplete = !!currentEditorState.weddingParty.length;
  const QAndAComplete = !!currentEditorState.faqs
    .map((faq) => !!faq.answer && !!faq.question)
    .some((a) => a);

  const galleryComplete = !!currentEditorState.gallery.length;
  const giftingComplete = !!(
    currentEditorState.bankAccounts.length ||
    currentEditorState.paymentLinks.length
  );
  const shareWebsiteComplete = !!currentEditorState.hasSharedWebsite;

  // aso ebi
  const asoEbiComplete = !!AsoEbiStore?.asoEbis?.length;

  const shouldDisplayPreview =
    hashtagComplete ||
    loveStoryComplete ||
    eventSchedulesComplete ||
    galleryComplete ||
    weddingPartyComplete ||
    giftingComplete ||
    QAndAComplete ||
    asoEbiComplete;

  return {
    hashtagComplete,
    loveStoryComplete,
    eventSchedulesComplete,
    galleryComplete,
    weddingPartyComplete,
    giftingComplete,
    QAndAComplete,
    shareWebsiteComplete,
    shouldDisplayPreview,
    asoEbiComplete,
  };
};

const createDefaultEditorState = (): Editor => ({
  template: "b",
  coupleNameOrder: "bride-first",
  accountID: "",
  mailRemindersMap: {
    [MailType.AccessCard]: "",
    [MailType.Countdown]: "",
    [MailType.ThankYouNote]: "",
    [MailType.GuestInvite]: "",
    [MailType.RsvpReminder]: "",
  },
  accessCards: {
    date: new Date(),
    backgroundCtx: "color",
    backgroundColor: {
      colorCode: "#FFF0F",
      colorName: "Light Yellow",
    },
    textColor: {
      colorName: "Black",
      colorCode: "#000",
    },
    backgroundImage: "",
    address: "",
    hideDetails: false,
    showGuestName: false,
    showAdmitsOneOnly: false,
    showEventName: true,
    eventId: "",
    selectedEventType: "",
    qrPosition: "center",
    qrCustomPosition: { x: 50, y: 50 },
    visibility: {
      showCoupleNames: true,
      showGuestName: true,
      showDate: true,
      showVenue: true,
      showWeddingOfLabel: true,
    },
    overlayOpacity: 0.7,
    overlayColor: { colorName: "Black", colorCode: "#000000" },
  },
  ubxdid: "",
  hasSharedWebsite: false,
  weddingID: "",
  preference: createDefaultWeddingPreference("Jill", "Jack", "June 5, 1992"),
  mainContent: {
    date: new Date(),
    hashtag: "",
    coupleLoveStory: "",
    coupleImage: "",
    logo: "",
    shouldDisplayLogo: false,
    useHashtagAsHeaderText: false,
  },
  clickedDdayGettingStarted: true, // keeps popping up during refresh.
  currentEventSchedule: createDefaultEventSchedule(),
  currentEventScheduleIndex: 0,
  welcomeNote: "",
  welcomeNoteDisplayStatus:
    WelcomeNoteDisplayStatus.WelcomeNoteDisplayStatusHide,
  guestLinkStatus: GuestLinkStatus.Activated,
  maxAdditionalGuestsPerInvite: 5,
  guestRestrictRSVPStatus: GuestRestrictRSVPStatus.Deactivated,
  resolveRestrictedRSVPWithEmail: ResolveRestrictedRSVPWithEmail.Deactivated,
  getPhoneWithRSVPStatus: GetPhoneWithRSVPStatus.Deactivated,
  getEmailWithRSVPStatus: GetEmailWithRSVPStatus.Deactivated,
  loveWallStatus: LoveWallStatus.Activated,
  loveWallStandaloneStatus: LoveWallStatus.Deactivated,
  loveWallRestrictionStatus: LoveWallRestrictionStatus.Active,
  brideAndGroomShorts: {
    brideShortWords: "",
    brideImage: "",
    groomShortWords: "",
    groomImage: "",
  },
  eventSchedules: [],
  gallery: [],
  currentWeddingPartyIndex: 0,
  currentWeddingParty: createNewWeddingParty(),
  weddingParty: [],
  personalInformation: [],
  rsvpRelationships: [],
  bankAccounts: [],
  paymentLinks: [],
  wishlistURL: "",
  giftingMessage: "",
  weddingPartyOrder: [],
  faqs: [],
  menu: [],
  programme: [],
  currentFAQ: createDefaultFAQ(),
  currentFAQIndex: undefined as any,
  currentGallery: createNewGallerySection(),
  creatorRole: UserWeddingRole.Groom,
  groomFirstName: "",
  groomLastName: "",
  groomEmail: "",
  brideFirstName: "",
  brideLastName: "",
  brideEmail: "",
  groomID: "",
  brideID: "",
  // spouseJoinedAt: "",
  // spouseInvitedAt: "",
  groomInvitedAt: "",
  brideInvitedAt: "",
  groomJoinedAt: "",
  brideJoinedAt: "",
  eventPlannerInvitedAt: "",
  eventPlannerJoinedAt: "",
  currentGalleryIndex: 0,
  currentMenu: createDefaultMenu(),
  currentProgram: createDefaultProgram(),
  currentBankAccount: createDefaultBankAccount(),
  currentPaymentLink: createDefaultPaymentLink(),
  currentBankAccountIndex: undefined as any,
  currentPaymentLinkIndex: undefined as any,
  isCountdownDrawerOpen: false,
  isScheduleDrawerOpen: false,
  // isDeleteDrawerOpen: false,
  // isEmailVerDrawerOpen: false,
  // isPasswordDrawerOpen: false,
  isUpdatingWeddingInfo: false,
  showHashTagOnDDay: false,
  XAccountHash: "",
  sendRSVPEmailStatus: SendRSVPEmailStatus.Immediately,
  currentMenuOptionItem: createDefaultMenuOptionItem(),
});

export const editorState = proxy<Editor>(createDefaultEditorState());
export const editorStateCapture = proxy<Editor>(createDefaultEditorState());

export const updateEditorCtxWithEditorData = (
  ctx: "original" | "capture",
  data: Editor,
) => {
  (ctx === "original"
    ? editorState
    : editorStateCapture
  ).isUpdatingWeddingInfo = false;
  (ctx === "original"
    ? editorState
    : editorStateCapture
  ).mainContent.coupleImage = data.mainContent.coupleImage;
  // dashboardStore.data.coupleImage = data.coupleImage;

  (ctx === "original" ? editorState : editorStateCapture).groomFirstName =
    data.groomFirstName;
  (ctx === "original" ? editorState : editorStateCapture).groomLastName =
    data.groomLastName;
  // dashboardStore.data.groomName = data.groomName;
  (ctx === "original" ? editorState : editorStateCapture).groomEmail =
    data.groomEmail;
  (ctx === "original" ? editorState : editorStateCapture).brideFirstName =
    data.brideFirstName;
  (ctx === "original" ? editorState : editorStateCapture).brideLastName =
    data.brideLastName;
  // dashboardStore.data.brideName = data.brideName;
  (ctx === "original" ? editorState : editorStateCapture).brideEmail =
    data.brideEmail;

  // dashboardStore.data.date = data.mainContent.date as any;
  const targetState = ctx === "original" ? editorState : editorStateCapture;
  targetState.mainContent = {
    ...targetState.mainContent,
    date: data.mainContent.date,
  };
  (ctx === "original" ? editorState : editorStateCapture).showHashTagOnDDay =
    data.showHashTagOnDDay;
};

export const updateEditorCapture = (capture: Editor) => {
  editorState.clickedDdayGettingStarted = capture.clickedDdayGettingStarted;
  editorStateCapture.template = capture.template;
  editorStateCapture.coupleNameOrder = capture.coupleNameOrder || "bride-first";
  editorStateCapture.sendRSVPEmailStatus = capture.sendRSVPEmailStatus;
  editorStateCapture.weddingID = capture.weddingID;
  editorStateCapture.creatorRole = capture.creatorRole;
  editorStateCapture.groomFirstName = capture.groomFirstName;
  editorStateCapture.groomLastName = capture.groomLastName;
  editorStateCapture.groomEmail = capture.groomEmail;
  editorStateCapture.brideFirstName = capture.brideFirstName;
  editorStateCapture.brideLastName = capture.brideLastName;
  editorStateCapture.brideEmail = capture.brideEmail;
  editorStateCapture.welcomeNote = capture.welcomeNote;
  editorStateCapture.welcomeNoteDisplayStatus =
    capture.welcomeNoteDisplayStatus;
  editorStateCapture.mainContent.coupleImage = capture.mainContent.coupleImage;
  editorStateCapture.ubxdid = capture.ubxdid;
  editorStateCapture.hasSharedWebsite = capture.hasSharedWebsite;
  if (capture.accessCards) {
    editorStateCapture.accessCards = {
      ...capture.accessCards,
      date: capture.accessCards.date ? new Date(capture.accessCards.date) : new Date(),
    };
  }
  editorStateCapture.mainContent = {
    date: capture.mainContent.date
      ? new Date(capture.mainContent.date)
      : ("" as any),
    hashtag: capture.mainContent.hashtag,
    coupleLoveStory: capture.mainContent.coupleLoveStory,
    coupleImage: capture.mainContent.coupleImage,
    useHashtagAsHeaderText: capture.mainContent.useHashtagAsHeaderText,
    logo: capture.mainContent.logo,
    shouldDisplayLogo: capture.mainContent.shouldDisplayLogo,
  };
  editorStateCapture.brideAndGroomShorts = {
    brideShortWords: capture.brideAndGroomShorts.brideShortWords,
    brideImage: capture.brideAndGroomShorts.brideImage,
    groomShortWords: capture.brideAndGroomShorts.groomShortWords,
    groomImage: capture.brideAndGroomShorts.groomImage,
  };
  editorStateCapture.eventSchedules = capture.eventSchedules || [];
  editorStateCapture.gallery = capture.gallery || [];
  editorStateCapture.personalInformation = capture.personalInformation || [];
  editorStateCapture.rsvpRelationships = capture.rsvpRelationships || [];
  editorStateCapture.bankAccounts = capture.bankAccounts || [];
  editorStateCapture.paymentLinks = capture.paymentLinks || [];
  editorStateCapture.wishlistURL = capture.wishlistURL || "";
  editorStateCapture.giftingMessage = capture.giftingMessage || "";
  editorStateCapture.faqs = capture.faqs || [];
  editorStateCapture.menu = capture.menu || [];
  if (capture.accessCards) {
    editorStateCapture.accessCards = {
      ...capture.accessCards,
      date: capture.accessCards.date ? new Date(capture.accessCards.date) : new Date(),
    };
  }
  editorStateCapture.programme = capture.programme || [];
  // editorStateCapture.spouseJoinedAt = capture.spouseJoinedAt;
  // editorStateCapture.spouseInvitedAt = capture.spouseInvitedAt;
  editorStateCapture.brideInvitedAt = capture.brideInvitedAt;
  editorStateCapture.brideJoinedAt = capture.brideJoinedAt;
  editorStateCapture.groomInvitedAt = capture.groomInvitedAt;
  editorStateCapture.groomJoinedAt = capture.groomJoinedAt;
  editorStateCapture.eventPlannerInvitedAt = capture.eventPlannerInvitedAt;
  editorStateCapture.eventPlannerJoinedAt = capture.eventPlannerJoinedAt;
  editorStateCapture.groomID = capture.groomID;
  editorStateCapture.brideID = capture.brideID;
  editorStateCapture.showHashTagOnDDay = capture.showHashTagOnDDay;
  return editorStateCapture;
};

export const updateEditor = (wedding: Wedding) => {
  ClientStorage.store(
    ClientStorageItem.WeddingVersion,
    String(wedding.version ?? 0),
  );
  editorState.clickedDdayGettingStarted = wedding.clickedDdayGettingStarted;
  editorState.template = wedding.template;
  editorState.weddingID = wedding._id;
  editorStateCapture.sendRSVPEmailStatus = wedding.sendRSVPEmailStatus;
  editorState.accountID = wedding.accountID;
  editorState.creatorRole = wedding.creatorRole;
  editorState.groomFirstName = wedding.groomFirstName;
  editorState.groomLastName = wedding.groomLastName;
  editorState.groomEmail = wedding.groomEmail;
  editorState.brideFirstName = wedding.brideFirstName;
  editorState.brideLastName = wedding.brideLastName;
  editorState.brideEmail = wedding.brideEmail;
  editorState.coupleNameOrder = wedding.coupleNameOrder || "bride-first";
  editorState.welcomeNote = wedding.welcomeNote;
  editorState.welcomeNoteDisplayStatus = wedding.welcomeNoteDisplayStatus;
  // console.log({update_wedding_date: wedding.date});
  const stubPreference = createDefaultWeddingPreference(
    wedding.brideFirstName,
    wedding.groomFirstName,
    formatDate(new Date(wedding.date)),
    wedding.coupleNameOrder || "bride-first",
  );
  editorState.preference = wedding.preference;
  if (wedding.preference.heroSection.values.hero_main_text.isDefault) {
    editorState.preference.heroSection.values.hero_main_text.text =
      stubPreference.heroSection.values.hero_main_text.text;
  }
  if (wedding.preference.heroSection.values.short_description.isDefault) {
    editorState.preference.heroSection.values.short_description.text =
      stubPreference.heroSection.values.short_description.text;
  }
  if (wedding.preference.heroSection.values.short_description.isDefault) {
    editorState.preference.heroSection.values.short_description.text =
      stubPreference.heroSection.values.short_description.text;
  }
  if (wedding.preference.heroSection.values.wedding_date.isDefault) {
    editorState.preference.heroSection.values.wedding_date.text =
      stubPreference.heroSection.values.wedding_date.text;
  }
  editorState.mailRemindersMap = {
    [MailType.AccessCard]:
      wedding?.mailRemindersMap?.[MailType.AccessCard] || "",
    [MailType.Countdown]: wedding?.mailRemindersMap?.[MailType.Countdown] || "",
    [MailType.ThankYouNote]:
      wedding?.mailRemindersMap?.[MailType.ThankYouNote] || "",
    [MailType.GuestInvite]:
      wedding?.mailRemindersMap?.[MailType.GuestInvite] || "",
    [MailType.RsvpReminder]:
      wedding?.mailRemindersMap?.[MailType.RsvpReminder] || "",
  };
  if (wedding.XAccountHash) {
    editorState.XAccountHash = wedding.XAccountHash;
  }
  if (wedding.loveWallStatus) {
    editorState.loveWallStatus = wedding.loveWallStatus;
  }
  editorState.loveWallStandaloneStatus =
    wedding.loveWallStandaloneStatus || wedding.loveWallStatus || LoveWallStatus.Deactivated;
  if (wedding.loveWallRestrictionStatus) {
    editorState.loveWallRestrictionStatus = wedding.loveWallRestrictionStatus;
  }
  if (wedding.guestLinkStatus) {
    editorState.guestLinkStatus = wedding.guestLinkStatus;
  }
  editorState.maxAdditionalGuestsPerInvite =
    wedding.maxAdditionalGuestsPerInvite > 0
      ? wedding.maxAdditionalGuestsPerInvite
      : 5;
  if (wedding.guestRestrictRSVPStatus) {
    editorState.guestRestrictRSVPStatus = wedding.guestRestrictRSVPStatus;
  }
  if (wedding.resolveRestrictedRSVPWithEmail) {
    editorState.resolveRestrictedRSVPWithEmail =
      wedding.resolveRestrictedRSVPWithEmail;
  }
  if (wedding.getPhoneWithRSVPStatus) {
    editorState.getPhoneWithRSVPStatus = wedding.getPhoneWithRSVPStatus;
  }
  if (wedding.getEmailWithRSVPStatus) {
    editorState.getEmailWithRSVPStatus = wedding.getEmailWithRSVPStatus;
  }
  editorState.mainContent.coupleImage = wedding.coupleImage;
  if (wedding.accessCards) {
    editorState.accessCards = {
      ...wedding.accessCards,
      date: wedding.accessCards.date ? new Date(wedding.accessCards.date) : new Date(),
    };
  }
  editorState.ubxdid = wedding.ubxdid;
  editorState.weddingPartyOrder = wedding.weddingPartyOrder;
  editorState.hasSharedWebsite = wedding.hasSharedWebsite;
  editorState.mainContent = {
    date: wedding.date ? new Date(wedding.date) : ("" as any),
    hashtag: wedding.hashtag,
    coupleLoveStory: wedding.coupleLoveStory,
    coupleImage: wedding.coupleImage,
    useHashtagAsHeaderText: wedding.useHashtagAsHeaderText,
    logo: wedding.logo,
    shouldDisplayLogo: wedding.shouldDisplayLogo,
  };
  editorState.brideAndGroomShorts = {
    brideShortWords: wedding.brideShortWords,
    brideImage: wedding.brideImage,
    groomShortWords: wedding.groomShortWords,
    groomImage: wedding.groomImage,
  };
  editorState.eventSchedules = wedding.eventSchedules || [];
  editorState.gallery = wedding.gallery || [];
  editorState.personalInformation = wedding.personalInformation || [];
  editorState.rsvpRelationships = wedding.rsvpRelationships || [];
  editorState.bankAccounts = wedding.bankAccounts || [];
  editorState.paymentLinks = wedding.paymentLinks || [];
  editorState.wishlistURL = wedding.wishlistURL || "";
  editorState.giftingMessage = wedding.giftingMessage || "";
  editorState.menu = wedding.menu || [];
  editorState.programme = wedding.programme || [];
  editorState.faqs = wedding.faqs || [];
  // editorState.spouseJoinedAt = wedding.spouseJoinedAt;
  // editorState.spouseInvitedAt = wedding.spouseInvitedAt;
  editorState.brideInvitedAt = wedding.brideInvitedAt;
  editorState.brideJoinedAt = wedding.brideJoinedAt;
  editorState.groomInvitedAt = wedding.groomInvitedAt;
  editorState.groomJoinedAt = wedding.groomJoinedAt;
  editorState.eventPlannerInvitedAt = wedding.eventPlannerInvitedAt;
  editorState.eventPlannerJoinedAt = wedding.eventPlannerJoinedAt;
  editorState.groomID = wedding.groomID;
  editorState.brideID = wedding.brideID;
  editorState.showHashTagOnDDay = wedding.showHashtagOnDDay;
  return editorState;
};

export enum WebsiteView {
  Template = "template",
  Content = "content",
  Preview = "preview",
  Appearance = "appearance",
}

export enum WebsiteNav {
  Hashtag = "The Basics",
  LoveStory = "Love story",
  EventSchedule = "Schedule",
  Gallery = "Gallery",
  WeddingParty = "Wedding Party",
  Gifting = "Gifting",
  AsoEbi = "Aso Ebi",
  QAndA = "Q & A",
}

export const websiteNextAction = (websiteNav: WebsiteNav) => {
  const move = {
    [WebsiteNav.Hashtag]: WebsiteNav.LoveStory,
    [WebsiteNav.LoveStory]: WebsiteNav.EventSchedule,
    [WebsiteNav.EventSchedule]: WebsiteNav.Gallery,
    [WebsiteNav.Gallery]: WebsiteNav.WeddingParty,
    [WebsiteNav.WeddingParty]: WebsiteNav.Gifting,
    [WebsiteNav.Gifting]: WebsiteNav.AsoEbi,
    [WebsiteNav.AsoEbi]: WebsiteNav.QAndA,
    [WebsiteNav.QAndA]: WebsiteNav.QAndA,
  };
  WebsiteStore.websiteNav = move[websiteNav];
};

export const websiteBackAction = (websiteNav: WebsiteNav) => {
  const move = {
    [WebsiteNav.Hashtag]: WebsiteNav.Hashtag,
    [WebsiteNav.LoveStory]: WebsiteNav.Hashtag,
    [WebsiteNav.EventSchedule]: WebsiteNav.LoveStory,
    [WebsiteNav.Gallery]: WebsiteNav.EventSchedule,
    [WebsiteNav.WeddingParty]: WebsiteNav.Gallery,
    [WebsiteNav.Gifting]: WebsiteNav.WeddingParty,
    [WebsiteNav.AsoEbi]: WebsiteNav.Gifting,
    [WebsiteNav.QAndA]: WebsiteNav.AsoEbi,
  };
  WebsiteStore.websiteNav = move[websiteNav];
};

export const WebsiteNavSubTextMap = {
  [WebsiteNav.Hashtag]: "",
  [WebsiteNav.LoveStory]: "",
  [WebsiteNav.EventSchedule]: "",
  [WebsiteNav.Gallery]: "",
  [WebsiteNav.WeddingParty]: "",
  [WebsiteNav.Gifting]: "Choose how to receive gifts",
  [WebsiteNav.AsoEbi]: "",
  [WebsiteNav.QAndA]: "Answer questions your guests may have.",
};

export const WebsiteStore = proxy<{
  websiteView: WebsiteView;
  websiteNav: WebsiteNav;
  isEventScheduleDrawerOpen: boolean;
  isWeddingPartyDrawerOpen: boolean;
  isGalleryDrawerOpen: boolean;
  isProgramDrawerOpen: boolean;
  isMenuDrawerOpen: boolean;
  isGiftDrawerOpen: boolean;
  isCheckInDrawerOpen: boolean;
  isQAndADrawerOpen: boolean;
  isChangeTemplateDrawerOpen: boolean;
  eventScheduleDrawerOpenCtx: "edit" | "create";
  weddingPartyDrawerOpenCtx: "edit" | "create";
  galleryDrawerOpenCtx: "edit" | "create";
  qAndADrawerOpenCtx: "edit" | "create";
  changeTemplateDrawerOpenCtx: "edit" | "create";
  isGiftingDrawerOpen: boolean;
  analyticDrawerOpen: boolean;
  customUrlDrawer: boolean;
  isDday: boolean;
  isUpdatingCustomUrl: boolean;
  giftingDrawerSelectCtx: "bank_account" | "payment_link" | "wishlist";
  giftingDrawerOpenCtx: "edit" | "create";
  analyticDrawerOpenCtx: "insight" | "share";
  isShareModalOpen: boolean;
  isAccessCardFormDrawerOpen: boolean;
  isEmailTemplateDrawerOpen: boolean;
  isLoveWallConfirmationModalOpen: boolean;
  isLoveWallModalOpen: boolean;
  isManagePostModalOpen: boolean;
  isUpdateInformationModalOpen: boolean;
  insights: {
    pageviews: number;
    rsvps: number;
  };
  ddayInsights: {
    pageviews: number;
    posts: number;
  };
}>({
  websiteView: null as any,
  websiteNav: WebsiteNav.Hashtag,
  isEventScheduleDrawerOpen: false,
  isWeddingPartyDrawerOpen: false,
  isProgramDrawerOpen: false,
  isMenuDrawerOpen: false,
  isGiftDrawerOpen: false,
  isCheckInDrawerOpen: false,
  isGalleryDrawerOpen: false,
  isQAndADrawerOpen: false,
  isChangeTemplateDrawerOpen: false,
  analyticDrawerOpen: false,
  eventScheduleDrawerOpenCtx: "create",
  weddingPartyDrawerOpenCtx: "create",
  galleryDrawerOpenCtx: "create",
  qAndADrawerOpenCtx: "create",
  changeTemplateDrawerOpenCtx: "create",
  isGiftingDrawerOpen: false,
  customUrlDrawer: false,
  isUpdatingCustomUrl: false,
  isDday: false,
  isManagePostModalOpen: false,
  giftingDrawerSelectCtx: "bank_account",
  analyticDrawerOpenCtx: "insight",
  giftingDrawerOpenCtx: "create",
  isShareModalOpen: false,
  isLoveWallModalOpen: false,
  isAccessCardFormDrawerOpen: false,
  isEmailTemplateDrawerOpen: false,
  isLoveWallConfirmationModalOpen: false,
  isUpdateInformationModalOpen: false,
  insights: {
    pageviews: 0,
    rsvps: 0,
  },
  ddayInsights: {
    pageviews: 0,
    posts: 0,
  },
});

// Modal Functions
export const onUpdateInformationModal = (state: "open" | "close") => () => {
  WebsiteStore.isUpdateInformationModalOpen = state === "open";
};

export const onLoveWallConfirmationModal = (state: "open" | "close") => () => {
  WebsiteStore.isLoveWallConfirmationModalOpen = state === "open";
};

export const onShareModal = (state: "open" | "close") => () => {
  WebsiteStore.isShareModalOpen = state === "open";
};

export const onAnalyticsDrawer = (state: "open" | "close") => () => {
  WebsiteStore.analyticDrawerOpen = state === "open";
};

export const onProgramModal = (state: "open" | "close") => () => {
  // console.log("testing it");
  WebsiteStore.isProgramDrawerOpen = state === "open";
  // console.log({ isProgramDrawerOpen: WebsiteStore.isProgramDrawerOpen });
};

export const onMenuModal = (state: "open" | "close") => () => {
  WebsiteStore.isMenuDrawerOpen = state === "open";
};

export const onGiftModal = (state: "open" | "close") => () => {
  WebsiteStore.isGiftDrawerOpen = state === "open";
};

export const onCheckInModal = (state: "open" | "close") => () => {
  WebsiteStore.isCheckInDrawerOpen = state === "open";
};

export const onCustomUrlDrawer = (state: "open" | "close") => () => {
  WebsiteStore.customUrlDrawer = state === "open";
};

export const onEmailTemplateDrawer = (state: "open" | "close") => () => {
  WebsiteStore.isEmailTemplateDrawerOpen = state === "open";
};

export const cloneAppState = (): {
  wedding: Wedding;
  asoEbis: AsoEbi[];
  wellWishes: WellWish[];
  weddingParties: WeddingParty[];
} => {
  const {
    template,
    sendRSVPEmailStatus,
    ubxdid,
    accountID,
    weddingID,
    accessCards,
    hasSharedWebsite,
    mainContent,
    brideAndGroomShorts,
    // currentEventSchedule,
    // currentEventScheduleIndex,
    creatorRole,
    groomFirstName,
    groomLastName,
    groomEmail,
    brideFirstName,
    brideLastName,
    brideEmail,
    groomID,
    brideID,
    preference,
    mailRemindersMap,
    groomInvitedAt,
    brideInvitedAt,
    groomJoinedAt,
    brideJoinedAt,
    eventSchedules,
    gallery,
    personalInformation,
    rsvpRelationships,
    bankAccounts,
    programme,
    menu,
    paymentLinks,
    wishlistURL,
    giftingMessage,
    faqs,
    // currentFAQ,
    // currentFAQIndex,
    guestLinkStatus,
    guestRestrictRSVPStatus,
    resolveRestrictedRSVPWithEmail,
    getPhoneWithRSVPStatus,
    getEmailWithRSVPStatus,
    loveWallStatus,
    loveWallStandaloneStatus,
    loveWallRestrictionStatus,
    welcomeNote,
    welcomeNoteDisplayStatus,
    // currentGallery,
    // currentGalleryIndex,
    // isCountdownDrawerOpen,
    // isScheduleDrawerOpen,
    clickedDdayGettingStarted,
    // currentWeddingPartyIndex,
    // currentWeddingParty,
    weddingParty,
    weddingPartyOrder,
    eventPlannerInvitedAt,
    eventPlannerJoinedAt,
    // currentBankAccount,
    // currentPaymentLink,
    // currentBankAccountIndex,
    // currentPaymentLinkIndex,
    // currentProgram,
    // currentMenu,
    // isUpdatingWeddingInfo,
    showHashTagOnDDay,
    XAccountHash,
  } = JSON.parse(JSON.stringify(editorState)) as Editor;
  const {
    data: { wellWishes },
  } = JSON.parse(JSON.stringify(wellWishesStore)) as typeof wellWishesStore;

  const { asoEbis, asoEbiStatus, asoEbiTransactionCostBearer } = JSON.parse(
    JSON.stringify(AsoEbiStore),
  ) as typeof AsoEbiStore;

  const wedding: Wedding = {
    _id: weddingID,
    sendRSVPEmailStatus,
    asoEbiStatus,
    asoEbiTransactionCostBearer,
    accountID,
    ubxdid,
    template,
    date: mainContent.date,
    accessCards,
    hasSharedWebsite,
    weddingPartyOrder,
    useHashtagAsHeaderText: mainContent.useHashtagAsHeaderText,
    hashtag: mainContent.hashtag,
    showHashtagOnDDay: showHashTagOnDDay,
    logo: mainContent.logo,
    shouldDisplayLogo: mainContent.shouldDisplayLogo,
    coupleLoveStory: mainContent.coupleLoveStory,
    brideShortWords: brideAndGroomShorts.brideShortWords,
    brideImage: brideAndGroomShorts.brideImage,
    groomShortWords: brideAndGroomShorts.groomShortWords,
    groomImage: brideAndGroomShorts.groomImage,
    eventSchedules,
    programme,
    menu,
    gallery,
    personalInformation,
    rsvpRelationships,
    bankAccounts,
    paymentLinks,
    wishlistURL,
    faqs,
    guestLinkStatus,
    guestRestrictRSVPStatus,
    resolveRestrictedRSVPWithEmail,
    getPhoneWithRSVPStatus,
    getEmailWithRSVPStatus,
    loveWallStatus,
    loveWallRestrictionStatus,
    createdAt: new Date(),
    updatedAt: new Date(),
    guestManagers: [],
    clickedDdayGettingStarted,
    mailRemindersMap,
    preference,
    XAccountHash,

    groomID,
    brideID,
    creatorID: "",
    creatorRole,
    groomFirstName,
    groomLastName,
    groomEmail,
    brideFirstName,
    brideLastName,
    brideEmail,
    coupleImage: mainContent.coupleImage,
    groomInvitedAt,
    brideInvitedAt,
    groomJoinedAt,
    brideJoinedAt,
    eventPlannerInvitedAt,
    eventPlannerJoinedAt,

    welcomeNote,
    welcomeNoteDisplayStatus,
    giftingMessage,
    // creatorGender,
    // creatorContext,
  };
  const weddingParties: WeddingParty[] = weddingParty || [];
  return { wedding, weddingParties, asoEbis, wellWishes };
};

subscribe(editorState, () => {
  appState.stage++;
});

subscribe(wellWishesStore, () => {
  appState.stage++;
});

subscribe(AsoEbiStore, () => {
  appState.stage++;
});
export const appState = proxy({
  stage: 0,
  isMobile: false,
});

type HandleBreakPointValueProps = {
  sm?: any;
  xs?: any;
  base?: any;
  md?: any;
  lg?: any;
  xl?: any;
};
// break point function
export const bpFn = (breakpointvalues: HandleBreakPointValueProps): any => {
  if (appState.isMobile) return breakpointvalues.base;
  return breakpointvalues;
};

export const onQAndAModal = (state: "open" | "close") => () => {
  WebsiteStore.isQAndADrawerOpen = state === "open";
};

export const onChangeTemplateModal = (state: "open" | "close") => () => {
  WebsiteStore.isChangeTemplateDrawerOpen = state === "open";
};
